import { AtmosphericDataEnum } from "@bienesis/common";
import { Gauge, Loader2, TriangleAlert } from "lucide-react";
import { createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import "twin.macro";

import { useAtmosphericData } from "@/api/climate-data/queries";
import {
  AtmosphericData,
  ClimateDataGranularity,
} from "@/api/climate-data/types";
import { Card } from "../climate-data/range-selector";

type AtmosphericDataContextValue = {
  data: AtmosphericData;
  isFetching: boolean;
};

const AtmosphericDataContext =
  createContext<AtmosphericDataContextValue | null>(null);

export const useAtmosphericDataContext = () => {
  const ctx = useContext(AtmosphericDataContext);
  if (!ctx)
    throw new Error(
      "useAtmosphericDataContext must be used within AtmosphericDataLoader"
    );
  return ctx;
};

export const AtmosphericDataLoader = ({
  areaId,
  type,
  from,
  to,
  granularity,
  children,
}: {
  areaId: string;
  type: AtmosphericDataEnum;
  from: string;
  to: string;
  granularity: ClimateDataGranularity;
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const { data, isLoading, isError, isFetching } = useAtmosphericData({
    type,
    areaId,
    from,
    to,
    granularity,
  });

  if (isError) {
    return (
      <Card tw="flex items-center gap-2 px-4 py-3 text-sm text-red-600">
        <TriangleAlert tw="w-4 h-4" />
        {t("meteo.climateView.loadError")}
      </Card>
    );
  }

  if (isLoading || !data) {
    return (
      <Card tw="flex items-center justify-center gap-2 px-4 py-8 text-sm text-gray-500">
        <Loader2 tw="w-4 h-4 animate-spin" />
        {t("meteo.climateView.loading")}
      </Card>
    );
  }

  if (!data.series.length) {
    return (
      <Card tw="flex items-center justify-center gap-2 px-4 py-8 text-sm text-gray-500">
        <Gauge tw="w-4 h-4" />
        {t("meteo.atmosphericView.empty")}
      </Card>
    );
  }

  return (
    <AtmosphericDataContext.Provider value={{ data, isFetching }}>
      {children}
    </AtmosphericDataContext.Provider>
  );
};
